/**
 * PATH: apps/qa/src/pages/new-inspection/NewJobDetailsPopup.tsx
 * FILE_ID: QA-NEWINSPECTION-JOB-POPUP-001
 * PURPOSE: Create a draft job (title, client, report title, cover, participants)
 *          from the QA New Inspection wizard without leaving the job step.
 */
import { useEffect } from 'react'
import { Loader2, X } from 'lucide-react'
import ComboField from '@quotech/quote-builder/components/ComboField'
import { QaParticipantsEditor } from '@quotech/job-hub/jobs/qa'
import type { Job, ToastNotification } from './inspection-types'
import { useNewJobDraft, type ClientHit } from './useNewJobDraft'

export default function NewJobDetailsPopup({
  organisationId,
  onClose,
  onCreated,
  showToast,
}: {
  organisationId: string | number
  onClose: () => void
  onCreated: (job: Job) => void
  showToast: (toast: Omit<ToastNotification, 'id'>) => void
}) {
  const draft = useNewJobDraft({ organisationId, onClose, onCreated, showToast })

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="qa-new-job-title"
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <h2 id="qa-new-job-title" className="text-base font-semibold">Job Details</h2>
          <button
            type="button"
            aria-label="Close job details"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 content-start gap-3 overflow-y-auto p-4">
          <label className="block text-sm">
            <span className="mb-1 block">Job title</span>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => draft.setTitle(e.target.value)}
              disabled={!draft.titleReady}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <ComboField<ClientHit>
            label="Client"
            value={draft.clientLabel}
            placeholder="Search or create a client"
            minChars={1}
            search={draft.searchClients}
            getKey={(c) => c.id}
            getDisplay={(c) => c.client_name}
            renderItem={(c) => <span className="truncate">{c.client_name}</span>}
            onSelect={(c) => {
              draft.setClientId(c.id)
              draft.setClientLabel(c.client_name)
            }}
            onCommitText={(value) => {
              draft.setClientLabel(value)
              if (!value.trim()) draft.setClientId(null)
            }}
            onCreate={async (name) => {
              const created = await draft.createClient(name.trim())
              if (created) {
                draft.setClientId(created.id)
                draft.setClientLabel(created.client_name)
              }
              return created
            }}
            inputClassName="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />

          <label className="block text-sm">
            <span className="mb-1 block">Report title</span>
            <input
              type="text"
              value={draft.reportTitle}
              onChange={(e) => draft.setReportTitle(e.target.value)}
              placeholder="Inspection Plan"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <QaParticipantsEditor
            rows={draft.staged}
            search={draft.searchMembers}
            onAdd={draft.onAddParticipant}
            onRemove={draft.onRemoveParticipant}
          />
        </div>

        {draft.error ? (
          <p className="px-4 text-xs text-destructive" role="alert">{draft.error}</p>
        ) : null}

        <div className="flex shrink-0 justify-end gap-2 border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm text-muted-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!draft.canCreate}
            onClick={() => draft.createMutation.mutate()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {draft.createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Create job
          </button>
        </div>
      </div>
    </div>
  )
}
